<template>
    <template v-if="page">
        <div
            class="min-h-dvh tw-flex tw-flex-col tw-items-center tw-justify-center"
            :style="bgStyle">
            <Tag
                :color="page.color"
                :text="page.name" />
            <h1
                class="tw-mt-7 tw-w-11/12 tw-text-center xl:tw-w-10/12 xl:tw-text-4xl 2xl:tw-w-8/12 3xl:tw-text-5xl"
                v-html="page.bannerTitle"></h1>
            <div
                v-if="page.bannerButtons && page.bannerButtons.length"
                class="tw-mt-16 tw-flex tw-flex-col tw-items-center tw-gap-6 md:tw-flex-row">
                <v-btn
                    v-for="btn in page.bannerButtons"
                    :key="btn.id"
                    class="banner-btn tw-px-7 xl:tw-text-base"
                    :color="page.color"
                    elevation="0"
                    height="57"
                    rounded="pill"
                    :variant="btn.type">
                    <a
                        :href="btn.url"
                        target="_blank"
                        >{{ btn.text }}</a
                    >
                </v-btn>
            </div>
        </div>
        <div class="io-container tw-relative tw-z-20 tw-grid tw-grid-cols-1 tw-gap-4 tw-overflow-hidden tw-pt-8 max-xl:tw-gap-16 xl:tw-grid-cols-12 3xl:tw-pt-16">
            <div class="tw-relative tw-z-30 tw-order-1 xl:tw-col-span-6">
                <img
                    v-if="page.contentImage"
                    alt=""
                    class="tw-relative tw-left-1/2 tw-my-8 tw-max-h-96 tw-max-w-[calc(100%+100px)] -tw-translate-x-1/2 md:tw-hidden"
                    :src="useMedia(page.contentImage).original" />
                <h1
                    class="tw-mt-6"
                    v-html="page.contentTitle"></h1>
                <div
                    class="h3 tw-mt-3 md:tw-mt-6 lg:tw-mt-8"
                    v-html="page.contentDescription"></div>
            </div>
            <div class="tw-order-2 max-md:tw-hidden xl:tw-col-span-6">
                <img
                    v-if="page.contentImage"
                    alt=""
                    class="desktop-image tw-relative tw-z-10 tw-mx-auto tw-h-full max-xl:tw-max-h-96 max-md:tw-hidden"
                    :src="useMedia(page.contentImage).original" />
            </div>
        </div>
        <div
            v-if="hasFeatures"
            class="io-container">
            <h2
                v-if="page.featuresTitle"
                class="tw-text-center"
                v-html="page.featuresTitle"></h2>
            <h2
                v-else
                class="tw-text-center"
                v-html="$t('product.ourFeatures')"></h2>
            <h3
                v-if="page.featuresText"
                class="tw-text-center"
                v-html="page.featuresText"></h3>

            <div
                v-if="page.features && page.features.length"
                class="tw-mt-10 tw-grid tw-grow tw-gap-4 lg:tw-mt-14 lg:tw-gap-8 3xl:tw-gap-8"
                :class="getFeaturesGridSize(page.features.length)">
                <div
                    v-for="feature in page.features"
                    :key="feature.id"
                    class="tw-flex tw-h-full tw-w-full tw-flex-col tw-rounded-4xl tw-bg-opacity-10 tw-px-8 tw-py-10 lg:tw-px-12 lg:tw-py-16 3xl:tw-px-10 3xl:tw-py-12"
                    :class="feature.color ? `tw-bg-${feature.color}` : ''">
                    <img
                        v-if="feature.icon"
                        class="tw-w-12 3xl:tw-w-14"
                        :src="useMedia(feature.icon).small" />
                    <h4
                        class="tw-text-xl tw-font-semibold"
                        :class="feature.icon ? 'tw-mt-6 3xl:tw-mt-9' : ''">
                        {{ feature.title }}
                    </h4>
                    <p class="tw-mt-2 tw-font-light">
                        {{ feature.text }}
                    </p>
                </div>
            </div>
        </div>
        <div
            v-if="hasCharacteristics"
            class="io-container tw-relative tw-z-20 tw-grid tw-grid-cols-1 tw-gap-16 tw-overflow-hidden tw-pt-8 lg:tw-gap-8 lg:tw-pt-16 xl:tw-grid-cols-12 2xl:tw-gap-20">
            <div class="max-md:tw-hidden xl:tw-col-span-5">
                <img
                    alt=""
                    class="desktop-image tw-relative tw-z-10 tw-mx-auto tw-h-full tw-object-contain max-xl:tw-max-h-96 max-md:tw-hidden"
                    :src="useMedia(page.characteristicsImage).original" />
            </div>
            <div class="tw-relative tw-z-30 xl:tw-col-span-7">
                <img
                    alt=""
                    class="tw-relative tw-left-1/2 tw-my-8 tw-max-h-96 tw-max-w-[calc(100%+100px)] -tw-translate-x-1/2 tw-object-contain md:tw-hidden"
                    :src="useMedia(page.characteristicsImage).original" />
                <h2
                    v-if="page.characteristicsTitle"
                    class="tw-mb-0 max-md:tw-mt-6"
                    v-html="page.characteristicsTitle"></h2>
                <h2
                    v-else
                    class="tw-mb-0 max-md:tw-mt-6"
                    v-html="$t('product.generalFeatures')"></h2>

                <div
                    v-if="page.characteristicsText"
                    class="h3 tw-mt-3 md:tw-mt-6 lg:tw-mt-10"
                    v-html="page.characteristicsText"></div>
            </div>
            <div
                v-if="page.characteristics && page.characteristics.length"
                class="tw-mt-10 tw-grid tw-grid-cols-1 tw-gap-10 md:tw-grid-cols-2 xl:tw-col-span-12 xl:tw-grid-cols-3">
                <div
                    v-for="(carac, i) in page.characteristics"
                    :key="carac.id"
                    class="tw-flex tw-items-center tw-gap-5 tw-rounded-2xl tw-bg-blue-30 tw-px-6 tw-py-4"
                    :class="[i % 2 === 0 ? 'tw-flex-row' : 'max-md:tw-flex-row-reverse']">
                    <img
                        v-if="carac.icon"
                        alt=""
                        class="tw-size-10 tw-object-contain"
                        :src="useMedia(carac.icon).small" />
                    <div>
                        <h5 class="tw-text-base tw-text-blue-400 lg:tw-text-lg">{{ carac.title }}</h5>
                        <p class="tw-text-base tw-font-medium lg:tw-text-xl">{{ carac.text }}</p>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="page.news && page.news.length"
            class="io-container">
            <h2
                v-if="page.newsTitle"
                v-html="page.newsTitle"></h2>
            <h2 v-else v-html="$t('product.news')"></h2>

            <NewsSlider :news="page.news" />
        </div>
        <div
            v-if="page.partners && page.partners.length"
            class="io-container">
            <h2
                v-if="page.partnersTitle"
                class="tw-text-center"
                v-html="page.partnersTitle"></h2>
            <h2
                v-else
                class="tw-text-center">
                {{ $t('product.partners') }}
            </h2>
            <PartnersSlider :partners="page.partners" />
        </div>
        <div
            v-if="page.financedBy && page.financedBy.length"
            class="io-container">
            <h2
                v-if="page.financedByTitle"
                class="tw-text-center"
                v-html="page.financedByTitle"></h2>
            <h2
                v-else
                class="tw-text-center" v-html="$t('product.financedBy')"></h2>
            <PartnersSlider :partners="page.financedBy" />
        </div>
        <div
            v-if="page.bottomBannerTitle"
            class="bottom-banner tw-py-20"
            :style="{
                backgroundImage: `url(${useMedia(page.bottomBannerBackground).original})`,
            }">
            <div class="io-container tw-relative tw-my-0 tw-grid tw-grid-cols-1 tw-gap-y-8 lg:tw-grid-cols-12">
                <div class="max-lg:tw-order-2 max-lg:tw-text-center lg:tw-col-span-7">
                    <h2
                        class="max-md:tw-text-2xl sm:tw-w-8/12"
                        v-html="page.bottomBannerTitle"></h2>
                    <div class="tw-flex tw-gap-6 max-lg:tw-justify-center">
                        <v-btn
                            class="tw-h-12"
                            color="blue"
                            rounded="pill"
                            :to="{ name: 'Contacts' }"
                            >{{ $t('product.contactUs') }}</v-btn
                        >
                        <v-btn
                            v-for="btn in page.bottomBannerButtons"
                            :key="btn.id"
                            class="tw-h-12"
                            color="blue"
                            rounded="pill"
                            v-bind="urlProps(btn.url)"
                            :variant="btn.type"
                            >{{ btn.text }}</v-btn
                        >
                    </div>
                </div>
                <div class="tw-max-h-80 max-lg:tw-order-1 max-lg:tw-text-center lg:tw-col-span-5">
                    <img
                        alt=""
                        class="tw-h-full tw-w-full tw-object-contain"
                        :src="useMedia(page.bottomBannerImage).medium" />
                </div>
            </div>
        </div>
    </template>
    <template v-else>
        <!-- loader -->
        <Loader />
    </template>
</template>

<script setup lang="ts">
    import Tag from '@/components/Tag.vue';
    import NewsSlider from '@/components/NewsSlider.vue';
    import PartnersSlider from '@/components/PartnersSlider.vue';
    import { useAppStore } from '../../store/app';
    import { useMedia } from '../../composables/useMedia';
    import Loader from '@/components/Loader.vue';
    import { urlProps } from '@/composables/urlProps';
    import { useI18n } from 'vue-i18n';

    const { t } = useI18n();
    const $route = useRoute();
    const $router = useRouter();
    const appStore = useAppStore();

    const page = computed(() => {
        return appStore.getProductBySlug($route.params.slug as string);
    });

    const pageColor = computed(() => (page.value ? `rgb(var(--v-theme-${page.value.color}))` : ''));

    const bgStyle = computed(() => ({
        backgroundImage: `url(${useMedia(page.value.bannerImage).original})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    }));

    const hasFeatures = computed(() => page.value?.features?.length > 0 || page.value.featuresText);

    const hasCharacteristics = computed(() => page.value?.characteristics?.length > 0 || page.value.characteristicsText);

    function getFeaturesGridSize(length: number) {
        if (length === 1) return 'tw-grid-cols-1';
        if (length === 2) return 'tw-grid-cols-1 sm:tw-grid-cols-2';
        if (length === 3) return 'tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-3';
        if (length === 4) return 'tw-grid-cols-1 sm:tw-grid-cols-2 3xl:tw-grid-cols-4';
        return 'tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-3';
    }

    watchEffect(() => {
        if (!page.value && appStore.hasFinishedLoading) {
            $router.push({ name: 'NotFound' });
        }
    });

    watchEffect(() => {
        // set the css variable --page-color
        if (pageColor.value) {
            document.documentElement.style.setProperty('--page-color', pageColor.value);
        }
    });

    watchEffect(() => {
        if (page.value?.name) {
            document.title = page.value?.name + ' - IOTech';
        } else {
            document.title = t('product.docTitle');
        }
    });
</script>

<style lang="scss">
    .banner-btn.v-btn--variant-outlined {
        border-width: 2px !important;
    }
</style>

<style lang="scss" scoped>
    .bottom-banner {
        @apply tw-relative tw-overflow-hidden tw-bg-cover tw-bg-center;

        & > * {
            @apply tw-z-10;
        }

        &::before {
            @apply tw-absolute tw-inset-0;
            content: '';
            z-index: 1;
            // background: url('@/assets/temp/ds.png');
            background-position: center;
            background-size: cover;
        }
    }
</style>
